import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle,
  Clock,
  Book,
  Video,
  Code,
  ExternalLink,
  Target,
  ArrowRight,
  PlayCircle,
  BookOpen,
  Briefcase,
  Award,
  Lightbulb,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Rocket,
  Trophy,
  Star,
  Zap,
  Calendar,
  Layout,
  Menu,
  X,
  Lock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { GridBackground } from '../components/TechUI';
import { careerAPI } from '../utils/api';
import SEO from '../components/SEO';

const DetailedRoadmapPage = () => {
  const navigate = useNavigate();
  const [roadmapData, setRoadmapData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [expandedTasks, setExpandedTasks] = useState(new Set());

  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  useEffect(() => {
    const loadRoadmapData = async () => {
      setRoadmapData(null);

      try {
        const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
        const career = JSON.parse(localStorage.getItem('selectedCareer') || '{}');
        const roadmapId = localStorage.getItem('selectedRoadmapId');

        if (roadmapId) {
          try {
            const roadmapResponse = await careerAPI.getUserRoadmaps();
            const savedRoadmap = roadmapResponse.roadmaps?.find(r => r.id === roadmapId);

            if (savedRoadmap && savedRoadmap.roadmapData) {
              if (!savedRoadmap.roadmapData.weeks || savedRoadmap.roadmapData.weeks.length === 0) {
                throw new Error('Backend roadmap data is invalid');
              }

              setRoadmapData(savedRoadmap.roadmapData);
              const completedTaskIds = savedRoadmap.completedTaskIds || [];
              setCompletedTasks(new Set(completedTaskIds));
            } else {
              const roadmap = JSON.parse(localStorage.getItem('roadmapData') || 'null');
              if (!roadmap || !roadmap.weeks || roadmap.weeks.length === 0) {
                throw new Error('No valid roadmap data found');
              }
              setRoadmapData(roadmap);
            }
          } catch (error) {
            console.error('Error loading roadmap from backend:', error);
            const roadmap = JSON.parse(localStorage.getItem('roadmapData') || 'null');
            if (!roadmap || !roadmap.weeks || roadmap.weeks.length === 0) {
              throw new Error('No roadmap data available');
            }
            setRoadmapData(roadmap);
          }
        } else {
          const roadmap = JSON.parse(localStorage.getItem('roadmapData') || 'null');
          if (!roadmap || !roadmap.weeks || !Array.isArray(roadmap.weeks) || roadmap.weeks.length === 0) {
            throw new Error('No roadmap data found. Please generate a roadmap first.');
          }
          setRoadmapData(roadmap);
        }

        setUserProfile(profile);
        setSelectedCareer(career);
      } catch (error) {
        console.error('Error loading roadmap data:', error);
        toast.error(error.message || 'Error loading roadmap data.');
        if (error.message.includes('regenerate') || error.message.includes('corrupted')) {
          setTimeout(() => navigate('/insights'), 3000);
        }
      }
    };

    loadRoadmapData();
  }, [navigate]);

  const handleTaskComplete = async (weekIndex, taskIndex) => {
    const taskId = `week_${weekIndex}_task_${taskIndex}`;
    const newCompleted = !completedTasks.has(taskId);

    setCompletedTasks(prev => {
      const newSet = new Set(prev);
      if (newCompleted) {
        newSet.add(taskId);
        // Check for week completion
        const weekTasks = roadmapData.weeks[weekIndex].tasks;
        const weekCompletedTasks = weekTasks.filter((_, idx) =>
          newSet.has(`week_${weekIndex}_task_${idx}`)
        ).length;

        if (weekCompletedTasks === weekTasks.length) {
          toast.success(`Week ${weekIndex + 1} Completed! Great job! 🎉`);
        }
      } else {
        newSet.delete(taskId);
      }
      return newSet;
    });

    const selectedRoadmapId = localStorage.getItem('selectedRoadmapId');
    if (selectedRoadmapId) {
      try {
        await careerAPI.updateTaskProgress(selectedRoadmapId, weekIndex, taskIndex, newCompleted);
      } catch (error) {
        console.error('Failed to sync task progress:', error);
        setCompletedTasks(prev => {
          const newSet = new Set(prev);
          if (!newCompleted) {
            newSet.add(taskId);
          } else {
            newSet.delete(taskId);
          }
          return newSet;
        });
        toast.error('Failed to sync progress. Please try again.');
      }
    }
  };

  const calculateProgress = () => {
    if (!roadmapData?.weeks) return 0;
    const totalTasks = roadmapData.weeks.reduce((total, week) =>
      total + (week.tasks?.length || 0), 0
    );
    return totalTasks > 0 ? Math.round((completedTasks.size / totalTasks) * 100) : 0;
  };

  const getWeekProgress = (weekIndex) => {
    const week = roadmapData?.weeks?.[weekIndex];
    if (!week?.tasks) return 0;
    const weekTasks = week.tasks.length;
    const completedWeekTasks = week.tasks.filter((_, taskIndex) =>
      completedTasks.has(`week_${weekIndex}_task_${taskIndex}`)
    ).length;
    return weekTasks > 0 ? Math.round((completedWeekTasks / weekTasks) * 100) : 0;
  };

  if (!roadmapData) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const progress = calculateProgress();
  const totalWeeks = roadmapData.weeks?.length || 0;
  const totalTasks = roadmapData.weeks?.reduce((sum, w) => sum + (w.tasks?.length || 0), 0) || 0;
  const currentWeek = roadmapData.weeks[selectedWeek];
  const currentWeekProgress = getWeekProgress(selectedWeek);

  return (
    <>
      <SEO
        title={`${selectedCareer?.title || 'Career'} Roadmap`}
        description={`Your personalized ${totalWeeks}-week journey to becoming a ${selectedCareer?.title || roadmapData.career_path}.`}
      />

      {/* Main Container - Fixed Height "Cockpit" */}
      <div className="h-[calc(100vh-64px)] bg-[#0B0F19] overflow-hidden flex flex-col md:flex-row">

        {/* Mobile Sidebar Toggle */}
        <div className="md:hidden p-4 border-b border-white/10 flex items-center justify-between bg-[#0B0F19]">
          <span className="font-mono font-bold text-white">Mission Control</span>
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 text-slate-400">
            {isSidebarOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Sidebar Navigation */}
        <AnimatePresence>
          {(isSidebarOpen || window.innerWidth >= 768) && (
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              className={`
                fixed md:relative z-20 w-full md:w-80 h-full bg-[#0F131F] border-r border-white/10 flex flex-col
                ${window.innerWidth < 768 ? 'absolute inset-0' : ''}
              `}
            >
              {/* Sidebar Header */}
              <div className="p-6 border-b border-white/10">
                <h2 className="text-xs font-mono uppercase text-slate-500 mb-2">Target Role</h2>
                <h1 className="text-xl font-bold text-white font-display leading-tight">
                  {selectedCareer?.title || roadmapData.career_path}
                </h1>

                {/* Overall Progress */}
                <div className="mt-6">
                  <div className="flex justify-between text-xs font-mono mb-2">
                    <span className="text-slate-400">Mission Progress</span>
                    <span className="text-emerald-400">{progress}%</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-1.5">
                    <div
                      className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Weeks List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                {roadmapData.weeks?.map((week, index) => {
                  const weekProgress = getWeekProgress(index);
                  const isCompleted = weekProgress === 100;
                  const isLocked = index > 0 && getWeekProgress(index - 1) < 100 && weekProgress === 0;
                  const isActive = selectedWeek === index;

                  return (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedWeek(index);
                        if (window.innerWidth < 768) setIsSidebarOpen(false);
                      }}
                      className={`w-full text-left p-3 rounded-xl border transition-all duration-200 group relative overflow-hidden ${isActive
                        ? 'bg-emerald-500/10 border-emerald-500/50'
                        : 'bg-slate-800/30 border-transparent hover:bg-slate-800/50'
                        }`}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" />
                      )}

                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-mono font-bold uppercase ${isActive ? 'text-emerald-400' : 'text-slate-500'
                          }`}>
                          Week {index + 1}
                        </span>
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : isLocked ? (
                          <Lock className="w-3 h-3 text-slate-600" />
                        ) : (
                          <span className="text-xs text-slate-500">{weekProgress}%</span>
                        )}
                      </div>

                      <div className={`text-sm font-medium truncate ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'
                        }`}>
                        {week.title}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Sidebar Footer */}
              <div className="p-4 border-t border-white/10 bg-[#0B0F19]/50">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition-colors py-2"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  Back to Dashboard
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto bg-grid-slate-900/[0.04] relative custom-scrollbar">
          <div className="absolute inset-0 bg-[#0B0F19] -z-10" />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10" />

          <div className="max-w-5xl mx-auto p-6 md:p-10">
            {/* Week Header */}
            <motion.div
              key={`header-${selectedWeek}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono font-bold uppercase">
                  Current Mission
                </span>
                <span className="text-slate-500 text-sm font-mono">
                  {currentWeek.timeCommitment || '10-15h'} Estimated
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-white font-display mb-4">
                {currentWeek.title}
              </h2>

              <p className="text-lg text-slate-400 leading-relaxed max-w-3xl">
                {currentWeek.description}
              </p>
            </motion.div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <div className="p-4 rounded-2xl bg-[#0F131F] border border-white/5">
                <div className="text-slate-500 text-xs font-mono uppercase mb-1">Tasks</div>
                <div className="text-2xl font-bold text-white">{currentWeek.tasks?.length || 0}</div>
              </div>
              <div className="p-4 rounded-2xl bg-[#0F131F] border border-white/5">
                <div className="text-slate-500 text-xs font-mono uppercase mb-1">Completed</div>
                <div className="text-2xl font-bold text-emerald-400">
                  {currentWeek.tasks?.filter((_, i) => completedTasks.has(`week_${selectedWeek}_task_${i}`)).length}
                </div>
              </div>
              <div className="p-4 rounded-2xl bg-[#0F131F] border border-white/5">
                <div className="text-slate-500 text-xs font-mono uppercase mb-1">Progress</div>
                <div className="text-2xl font-bold text-blue-400">{currentWeekProgress}%</div>
              </div>
              <div className="p-4 rounded-2xl bg-[#0F131F] border border-white/5">
                <div className="text-slate-500 text-xs font-mono uppercase mb-1">Status</div>
                <div className={`text-sm font-bold mt-1.5 ${currentWeekProgress === 100 ? 'text-emerald-400' : 'text-yellow-400'
                  }`}>
                  {currentWeekProgress === 100 ? 'COMPLETED' : 'IN PROGRESS'}
                </div>
              </div>
            </div>

            {/* Tasks List */}
            <div className="space-y-4">
              <h3 className="text-sm font-mono font-bold text-slate-500 uppercase mb-4">Mission Tasks</h3>

              {currentWeek.tasks?.map((task, taskIndex) => {
                const taskId = `week_${selectedWeek}_task_${taskIndex}`;
                const isTaskCompleted = completedTasks.has(taskId);

                return (
                  <motion.div
                    key={taskId}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: taskIndex * 0.05 }}
                    onClick={() => navigate(`/roadmap/${roadmapData?.id || roadmapData?.roadmap_id || 'current'}/week/${selectedWeek}/task/${taskIndex}`)}
                    className={`group p-5 rounded-2xl border cursor-pointer transition-all duration-300 ${isTaskCompleted
                      ? 'bg-emerald-900/5 border-emerald-500/20'
                      : 'bg-[#0F131F] border-white/5 hover:border-emerald-500/30 hover:bg-[#161b2c]'
                      }`}
                  >
                    <div className="flex items-start gap-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTaskComplete(selectedWeek, taskIndex);
                        }}
                        className={`mt-1 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all flex-shrink-0 ${isTaskCompleted
                          ? 'bg-emerald-500 border-emerald-500 scale-110'
                          : 'border-slate-600 hover:border-emerald-400 hover:bg-emerald-500/10'
                          }`}
                      >
                        {isTaskCompleted && <CheckCircle className="w-4 h-4 text-white" />}
                      </button>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold uppercase tracking-wider ${task.type === 'video' ? 'bg-red-500/10 text-red-400' :
                            task.type === 'reading' ? 'bg-blue-500/10 text-blue-400' :
                              task.type === 'practice' ? 'bg-emerald-500/10 text-emerald-400' :
                                'bg-purple-500/10 text-purple-400'
                            }`}>
                            {task.type}
                          </span>
                          <h4 className={`font-bold text-base ${isTaskCompleted ? 'text-slate-500 line-through' : 'text-white'
                            }`}>
                            {task.title}
                          </h4>
                        </div>

                        <p className="text-slate-400 text-sm leading-relaxed mb-4">
                          {task.description}
                        </p>

                        <div className="w-full mb-4 flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:bg-slate-700/50 transition-all group-hover:border-blue-500/30 text-left">
                          <div className="flex items-center gap-3">
                            <BookOpen className="w-5 h-5 text-blue-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                            <div>
                              <h5 className="text-sm font-bold text-blue-400 group-hover:text-blue-300 transition-colors">Read Deep Dive Walkthrough</h5>
                              <p className="text-xs text-slate-400 mt-0.5">Comprehensive multi-page tutorial with code examples</p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
                        </div>

                        {task.real_world_example && (
                          <div className="mb-5 p-4 rounded-xl bg-emerald-900/10 border border-emerald-500/20 flex gap-3">
                            <Briefcase className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <h5 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-1">Real World Example</h5>
                              <p className="text-slate-300 text-sm leading-relaxed">{task.real_world_example}</p>
                            </div>
                          </div>
                        )}

                        {/* Resources & Tips */}
                        <div className="flex flex-wrap gap-3">
                          {task.resources?.map((resource, idx) => (
                            <a
                              key={idx}
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-colors border border-white/5"
                            >
                              {resource.type === 'video' ? <PlayCircle className="w-3.5 h-3.5" /> :
                                resource.type === 'article' ? <BookOpen className="w-3.5 h-3.5" /> :
                                  <ExternalLink className="w-3.5 h-3.5" />}
                              {resource.title || 'Resource'}
                            </a>
                          ))}

                          {task.tips && (
                            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-yellow-400 bg-yellow-400/10 px-3 py-1.5 rounded-lg border border-yellow-400/20">
                              <Lightbulb className="w-3.5 h-3.5" />
                              Pro Tip
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Week Completion Celebration */}
            {currentWeekProgress === 100 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 p-8 rounded-3xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 text-center"
              >
                <Trophy className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Mission Accomplished!</h3>
                <p className="text-slate-400 mb-6">You've mastered everything in this module.</p>

                {selectedWeek < (roadmapData.weeks?.length || 0) - 1 ? (
                  <button
                    onClick={() => setSelectedWeek(prev => prev + 1)}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Start Next Mission <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Return to Base <Rocket className="w-4 h-4" />
                  </button>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailedRoadmapPage;
